import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import LandingPage from "./views/LandingPage.js";
import LoginPage from "./views/LoginPage.js";
import PrivacyPolicy from "./views/PrivacyPolicy.js";
import AboutUs from "./views/AboutUs";
import AuthLoading from "./views/AuthLoading";
import { Provider } from "react-redux";
import ProtectedRoute from "./views/ProtectedRoute";
import MyProfile from "./views/MyProfile";
import BookingHistory from "./views/BookingHistory";
import Dashboard from "./views/Dashboard";
import Pricing from "./views/Pricing";
import AddBookings from "./views/AddBookings";
import Promos from "./views/Promos";
import Users from "./views/Users";
import Notifications from "./views/Notifications";
import Settings from "./views/Settings.js";
import CancellationReasons from "./views/CancellationReasons";
import AddMoney from "./views/AddMoney";
import Withdraws from "./views/Withdraws";
import AllReports from "./views/AllReports";
import { FirebaseProvider, store } from "common";
import { FirebaseConfig } from "./config/FirebaseConfig";
import { GoogleMapApiConfig } from "./config/GoogleMapApiConfig";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import ContactUs from "./views/ContactUs";
import UserWallet from "./views/UserWallet";
import CarsList from "./views/CarsList";
import { ThemeProvider } from "@mui/styles";
import { createTheme } from "@mui/material";
import { useJsApiLoader } from "@react-google-maps/api";
import TermCondition from "views/TermCondition.js";
import Complain from "views/Complain.js";
import AdminSupportList from "./views/AdminSupportList";
import AdminSupportChat from "./views/AdminSupportChat";
import SubscriptionManagement from "views/SubscriptionManagement.js";
import UserSubscription from "./views/UserSubscription.js";
import DocumentManagementMenu from "views/DocumentManagementMenu.js";
import RolesManagement from "views/RolesManagement.js";

const libraries = ["geometry", "drawing", "places"];

i18n.use(initReactI18next).init({
    resources: {},
    fallbackLng: "en",
    ns: ["translations"],
    defaultNS: "translations",
    interpolation: {
        escapeValue: false,
    },
});

// Error Boundary Component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Error caught by boundary:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div
                    style={{
                        padding: "20px",
                        textAlign: "center",
                        fontFamily: "Arial, sans-serif",
                    }}
                >
                    <h2>Something went wrong.</h2>
                    <p>
                        The application encountered an error. Please refresh the
                        page.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            padding: "10px 20px",
                            backgroundColor: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                    >
                        Refresh Page
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

function App() {
    const { isLoaded, loadError } = useJsApiLoader({
        id: "google-map",
        googleMapsApiKey: GoogleMapApiConfig,
        libraries,
    });

    const theme = createTheme();

    // Handle Google Maps loading error
    if (loadError) {
        console.error("Google Maps API loading error:", loadError);
    }

    return (
        <ErrorBoundary>
            <Provider store={store}>
                <FirebaseProvider config={FirebaseConfig}>
                    <ThemeProvider theme={theme}>
                        <AuthLoading>
                            <BrowserRouter>
                                <Routes>
                                    <Route
                                        path="/dashboard"
                                        element={
                                            <ProtectedRoute
                                                permit={"admin,fleetadmin"}
                                            >
                                                <Dashboard />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/bookings"
                                        element={
                                            <ProtectedRoute
                                                permit={
                                                    "customer,admin,driver,fleetadmin"
                                                }
                                            >
                                                <BookingHistory />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/profile"
                                        element={
                                            <ProtectedRoute
                                                permit={
                                                    "customer,admin,driver,fleetadmin"
                                                }
                                            >
                                                <MyProfile />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/pricing"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <Pricing />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/cars"
                                        element={
                                            <ProtectedRoute
                                                permit={
                                                    "admin,fleetadmin,driver"
                                                }
                                            >
                                                <CarsList />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/cancelreasons"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <CancellationReasons />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/addbookings"
                                        element={
                                            <ProtectedRoute
                                                permit={
                                                    "admin,fleetadmin,customer"
                                                }
                                            >
                                                <AddBookings />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/promos"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <Promos />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/users"
                                        element={
                                            <ProtectedRoute
                                                permit={"admin,fleetadmin"}
                                            >
                                                <Users />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/notifications"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <Notifications />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/addtowallet"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <AddMoney />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/userwallet"
                                        element={
                                            <ProtectedRoute
                                                permit={
                                                    "customer,driver,fleetadmin"
                                                }
                                            >
                                                <UserWallet />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/withdraws"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <Withdraws />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/allreports"
                                        element={
                                            <ProtectedRoute
                                                permit={"admin,fleetadmin"}
                                            >
                                                <AllReports />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/complain"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <Complain />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/roles"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <RolesManagement />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/admin-support-list"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <AdminSupportList />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/admin-support-chat/:userId"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <AdminSupportChat />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/settings"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <Settings />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/subscriptions"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <SubscriptionManagement />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/user-subscription"
                                        element={
                                            <ProtectedRoute
                                                permit={"customer,driver"}
                                            >
                                                <UserSubscription />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/contact-us"
                                        element={<ContactUs />}
                                    />
                                    <Route
                                        path="/about-us"
                                        element={<AboutUs />}
                                    />
                                    <Route
                                        path="/privacy-policy"
                                        element={<PrivacyPolicy />}
                                    />
                                    <Route
                                        path="/term-condition"
                                        element={<TermCondition />}
                                    />
                                    <Route
                                        path="/login"
                                        element={<LoginPage />}
                                    />
                                    <Route path="/" element={<LandingPage />} />
                                    <Route
                                        path="/documentmanagement"
                                        element={
                                            <ProtectedRoute permit={"admin"}>
                                                <DocumentManagementMenu />
                                            </ProtectedRoute>
                                        }
                                    />
                                </Routes>
                            </BrowserRouter>
                        </AuthLoading>
                    </ThemeProvider>
                </FirebaseProvider>
            </Provider>
        </ErrorBoundary>
    );
}

export default App;
